import React from 'react'
import PropTypes from 'prop-types'

import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import ImageIcon from '@material-ui/icons/Image'

import Image from 'artkive/components/Image'
import ContainedSection from 'artkive/components/PageSections/ContainedSection'
import ProductDescriptionItem from 'artkive/components/PageSections/ProductDescriptionItem'
import { TrackButton } from 'artkive/components/Tracker'
import { GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useSale from 'artkive/hooks/useSale'
import { PRICING } from 'artkive/utils/constants'
import theme from 'artkive/utils/theme'

import Product from 'images/ecom/get-started/BoxProduct.png'
import ProductMobile from 'images/ecom/get-started/BoxProduct-mobile.png'

import { FooterNotice } from './GetStartedWithBox.style'

const useStyles = makeStyles(({ breakpoints }) => ({
  mainWrapper: {
    display: 'flex',
    alignItems: 'center',
    height: 800,
    background: 'linear-gradient(90deg, rgba(237,235,244,1) 50%, rgba(226,222,237,1) 50%);',
    [breakpoints.down('sm')]: {
      height: 'auto',
      background: 'rgba(237,235,244,1)',
    },
  },
  noticeWrapper: {
    padding: '56px 0',
    textAlign: 'center',
    backgroundColor: theme.colors.bg.sky,
    [breakpoints.down('sm')]: {
      padding: '32px 16px',
    },
  },
  description: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '0 120px 0 0',
    [breakpoints.down('sm')]: {
      padding: '32px 16px 64px',
    },
  },
  image: {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
    maxWidth: 'unset',
    [breakpoints.up('lg')]: {
      paddingLeft: 30,
    },
  },
  artBackTitle: {
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.primary.dark,
    lineHeight: 0.8,
  },
  subtitle: {},
  bullets: {},
  cta: {},
  footerNotice: {},
  noticeContainer: {},
}))

const productBullets = [
  { text: '24”x18”x4” crush-proof Artkive Box (holds 300-400 pieces of art)' },
  { text: 'Re-sealable art protection bag' },
  { text: 'Order form to select your Kids Art Books and/or Framed Mosaics, plus tips and tricks to organize your art' },
  { text: 'Prepaid shipping label to send back your box from anywhere in the contiguous U.S.' },
]

export const returnTimeline = (
  <>
    *Flexible box return timeline, send art within {PRICING.books.dueBackMonths} months or pay deposit for first{' '}
    {PRICING.books.minPages} pieces (${PRICING.books.base})
  </>
)

const defaultCaption = <Typography variant='caption'>{returnTimeline}. See details below.</Typography>

const GetStartedWithBox = ({ caption = defaultCaption, noticeTitle, classes: classesProp }) => {
  const classes = useStyles({ classes: classesProp })
  const isMobile = useMobileBreakpoint()
  const muiTheme = useTheme()
  const { isSale, priceLabel, boxCheckoutUrl } = useSale()

  const boxProps = { pt: isMobile ? 0 : undefined }

  const bullets = productBullets.map(({ text }, index) => (
    <ProductDescriptionItem key={index}>{text}</ProductDescriptionItem>
  ))

  return (
    <>
      <ContainedSection className={classes.mainWrapper} disableGutters={isMobile} py={0} {...boxProps}>
        <Grid container direction={isMobile ? 'column-reverse' : 'row'}>
          <Grid item xs md={6} className={classes.description}>
            <Typography variant='h2' align={isMobile ? 'center' : 'left'}>
              Get Started <br /> {isSale ? `With a ${priceLabel} Artkive Box` : 'With An Artkive Box'}
            </Typography>
            <Box mb={3} mt={isMobile ? 1 : 3}>
              <Typography variant='subtitle1' align={isMobile ? 'center' : 'left'} className={classes.subtitle}>
                Includes everything you need to send in your artwork
              </Typography>
            </Box>
            {/* Only wrap when a page styles the bullets, so the default markup stays the same on the other pages. */}
            {classesProp?.bullets ? <Box className={classes.bullets}>{bullets}</Box> : bullets}
            <Box
              display='flex'
              justifyContent={isMobile ? 'center' : 'left'}
              mt={isMobile ? 4 : 5}
              mb={2}
              className={classes.cta}
            >
              <TrackButton
                color='primary'
                href={boxCheckoutUrl}
                size='large'
                variant='contained'
                trackerEvent={GET_BOX_CLICK}
                trackerOptions={{
                  section: 'get_started_with_box_section',
                }}
              >
                {isSale ? `Get My ${priceLabel} Box` : 'Get My Box'}
              </TrackButton>
            </Box>
            {caption}
            <FooterNotice className={classes.footerNotice}>
              <Typography gutterBottom className={classes.artBackTitle}>
                <ImageIcon /> <strong>Want to get your artwork back?</strong>
              </Typography>
              <Typography variant='caption' component='div'>
                You can choose to have your art returned during checkout.
              </Typography>
            </FooterNotice>
          </Grid>
          <Grid item xs md={6}>
            <picture>
              <source srcSet={`${Product}`} media={muiTheme.breakpoints.up('md').replace('@media ', '')} />
              <Image src={ProductMobile} className={classes.image} />
            </picture>
          </Grid>
        </Grid>
      </ContainedSection>
      <ContainedSection
        className={classes.noticeWrapper}
        containerClassName={classes.noticeContainer}
        disableGutters={isMobile}
        py={0}
        {...boxProps}
      >
        {noticeTitle}
        <Typography>
          Please return your box within {PRICING.books.dueBackMonths} months. If you’re not ready to send your box back
          within {PRICING.books.dueBackMonths} months of your purchase date, no worries—we’ll simply process a $
          {PRICING.books.base} deposit at that time, which covers the first {PRICING.books.minPages} pieces in your
          book. This deposit will be applied toward your final order total once your box is sent in.
        </Typography>
      </ContainedSection>
    </>
  )
}

GetStartedWithBox.propTypes = {
  caption: PropTypes.node,
  noticeTitle: PropTypes.node,
  classes: PropTypes.object,
}

export default GetStartedWithBox
